// ProjectionOverlay.js — Value chart with actual (solid) and projected (dotted) paths.
// Shows daily marks for live simulation runs.

import React from 'react';

function formatCurrency(value, currency = 'USD') {
  if (value == null) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function MiniBar({ label, value, max, color = 'bg-blue-500' }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-gray-500 w-20 text-right">{label}</span>
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-gray-700 font-mono w-24 text-right">{formatCurrency(value)}</span>
    </div>
  );
}

export default function ProjectionOverlay({ dailyMarks = [], run, sourceCurrency = 'USD' }) {
  if (!dailyMarks.length) {
    return (
      <div className="text-xs text-gray-400 text-center py-8">
        Daily marks will appear once the simulation starts tracking.
      </div>
    );
  }

  const latest = dailyMarks[dailyMarks.length - 1];
  const initial = dailyMarks[0];
  const maxVal = Math.max(
    ...dailyMarks.map(m => Math.max(m.value_base_unhedged || 0, m.value_base_hedged || 0))
  );

  // Simple sparkline using CSS
  const chartHeight = 120;
  const chartWidth = '100%';

  return (
    <div className="space-y-4">
      {/* Value summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">Unhedged Value</p>
          <p className="text-lg font-semibold text-gray-900 font-mono">
            {formatCurrency(latest.value_base_unhedged, sourceCurrency)}
          </p>
          <p className="text-[10px] text-gray-400 mt-0.5">
            Spot: {latest.spot_rate ? parseFloat(latest.spot_rate).toFixed(2) : '-'}
          </p>
        </div>
        {run?.locked_forward_rate && (
          <div className="bg-blue-50 rounded-lg p-3">
            <p className="text-[10px] font-medium text-blue-600 uppercase tracking-wider">Hedged Value</p>
            <p className="text-lg font-semibold text-blue-900 font-mono">
              {formatCurrency(latest.value_base_hedged, sourceCurrency)}
            </p>
            <p className="text-[10px] text-blue-400 mt-0.5">
              Forward: {parseFloat(run.locked_forward_rate).toFixed(2)}
            </p>
          </div>
        )}
      </div>

      {/* Simplified chart — bar representation of daily marks */}
      <div>
        <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-2">
          Value Over Time ({sourceCurrency})
        </p>
        <div className="bg-gray-50 rounded-lg p-3 space-y-1.5" style={{ maxHeight: chartHeight + 60, overflow: 'auto' }}>
          {dailyMarks.slice(-14).map((mark, idx) => (
            <MiniBar
              key={idx}
              label={mark.mark_date ? mark.mark_date.slice(5) : `Day ${idx + 1}`}
              value={mark.value_base_unhedged}
              max={maxVal * 1.1}
              color={run?.locked_forward_rate ? 'bg-blue-400' : 'bg-emerald-400'}
            />
          ))}
        </div>
        {dailyMarks.length > 14 && (
          <p className="text-[10px] text-gray-400 mt-1">
            Showing last 14 of {dailyMarks.length} daily marks
          </p>
        )}
      </div>

      {/* Projected tag */}
      <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
        <div className="w-3 h-0.5 border-t-2 border-dashed border-amber-400" />
        <span>Projected values are estimates based on latest available data and will update daily.</span>
      </div>
    </div>
  );
}
